<script lang="ts">
import type { TabsItem } from '#layers/ui';
import type { ListingView } from '~/types/listing';

interface ListingViewsProps {
	views: ListingView[];
	defaultView: ListingView;
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { randomUUID } from '#layers/base/utils/uuid';
import { useBlockEditorLibrary } from '#layers/block-engine';
import { showConfirmationDialog } from '#layers/ui/utils/show-confirmation-dialog';
import { useListingViews } from '~/composables/listing/use-listing-views';

const props = defineProps<ListingViewsProps>();

const emits = defineEmits<{
	add: [view: ListingView['id']];
	edit: [view: ListingView['id']];
	duplicate: [view: ListingView['id']];
	deleteView: [view: ListingView['id']];
}>();

const appConfig = useAppConfig();
const { t } = useI18n();
const { getViewName } = useListingViews({ collectionId: null });

const current = defineModel<string>('currentViewId');

const blockLibraryStore = useBlockEditorLibrary();

// Combine default view with user views
const allViews = computed(() => [props.defaultView, ...props.views]);

const items = computed<TabsItem[]>(() => allViews.value.map((view) => {
	const block = blockLibraryStore.getBlock(view.layoutId);
	return {
		value: view.id,
		label: getViewName(view),
		icon: view.icon ?? block?.icon ?? undefined,
	};
}));

function createView() {
	const newViewId: ListingView['id'] = randomUUID();
	emits('add', newViewId);
	current.value = newViewId;
}

async function handleDelete(viewId: string) {
	const view = allViews.value.find((v) => v.id === viewId);
	if (!view) return;

	const viewName = getViewName(view);

	const confirmed = await showConfirmationDialog({
		props: {
			title: t('listing.views.delete.question', { name: viewName }),
			confirm: { color: 'error', label: t('listing.views.delete.confirm') },
		},
	});

	if (confirmed) {
		emits('deleteView', viewId);
	}
}

function getContextMenuItems(viewId: string) {
	const isDefaultView = viewId === props.defaultView.id;

	return [
		{
			label: t('common.edit'),
			icon: 'lucide:pencil',
			onSelect: () => emits('edit', viewId),
		},
		{
			label: t('common.duplicate'),
			icon: 'lucide:copy',
			onSelect: () => emits('duplicate', viewId),
		},
		{
			label: t('common.delete'),
			icon: 'lucide:trash',
			color: 'error' as const,
			disabled: isDefaultView,
			onSelect: () => handleDelete(viewId),
		},
	];
}

const contextMenuOpen = ref(false);
const contextMenuViewId = ref<string | null>(null);
const contextMenuPosition = ref({ x: 0, y: 0 });

const currentContextMenuItems = computed(() => {
	if (!contextMenuViewId.value) return [];
	return getContextMenuItems(contextMenuViewId.value);
});

// Virtual element for positioning the context menu at click location
const virtualReference = computed(() => ({
	getBoundingClientRect: () => ({
		x: contextMenuPosition.value.x,
		y: contextMenuPosition.value.y,
		width: 0,
		height: 0,
		top: contextMenuPosition.value.y,
		right: contextMenuPosition.value.x,
		bottom: contextMenuPosition.value.y,
		left: contextMenuPosition.value.x,
	}),
}));

// Handle right-click on tabs
function handleTabContextMenu(event: MouseEvent) {
	const tabButton = (event.target as HTMLElement).closest('[role="tab"]');
	const viewId = tabButton?.getAttribute('id')?.split('-trigger-')[1];

	if (!viewId) return;

	event.preventDefault();
	event.stopPropagation();

	contextMenuViewId.value = viewId;
	contextMenuPosition.value = { x: event.clientX, y: event.clientY };
	contextMenuOpen.value = true;
}

function handleContextMenuClose(open: boolean) {
	if (!open) {
		contextMenuViewId.value = null;
	}
}
</script>

<template>
	<div class="flex min-w-0 flex-1 items-center gap-2">
		<DTabs
			v-model="current"
			:items
			:ui="{ root: 'gap-0' }"
			size="sm"
			collapsible
			@contextmenu="handleTabContextMenu"
		/>
		<DTooltip :text="$t('listing.views.add_new')">
			<DButton
				variant="ghost"
				color="neutral"
				:leading-icon="appConfig.ui.icons.plus"
				:aria-label="$t('listing.views.add_new')"
				size="sm"
				@click="createView"
			/>
		</DTooltip>
	</div>
	<DDropdownMenu
		v-model:open="contextMenuOpen"
		:items="currentContextMenuItems"
		:content="{ reference: virtualReference }"
		size="sm"
		@update:open="handleContextMenuClose"
	/>
</template>
